/**
 * Synthesis agent — WP-S15.1.
 * Uses Opus via the LLM adapter to generate draft intelligence assessments
 * from validated entity graphs.
 *
 * Responsibilities:
 * - Build a structured graph summary from entities + relationships
 * - Construct an assessment template with required sections
 * - Call adapter.synthesize() (Opus 4.6)
 * - Validate that every section has entity citations
 * - Return a DraftAssessment with provenance enforcement
 *
 * The output is labeled "MACHINE-GENERATED DRAFT — REQUIRES ANALYST REVIEW".
 */

import type { LLMAdapter, RawDraftAssessment } from './llm-adapter';
import type { Entity, Relationship, EntityType } from '@/intelligence/entities/types';
import type { DraftAssessment, AssessmentSection, CoverageReport } from '@/intelligence/investigation/types';
import type { DeskConfig } from '@/config/desk-config';
import { saveAssessmentVersion } from '@/intelligence/investigation/investigation-store';

// ---------------------------------------------------------------------------
// Graph summary builder
// ---------------------------------------------------------------------------

/**
 * Build a structured text summary of the entity graph for the LLM.
 * Includes entity inventory, relationships, and coverage status.
 */
export function buildGraphSummary(
  entities: Entity[],
  relationships: Relationship[],
  coverage: CoverageReport | null,
): string {
  const sections: string[] = [];

  // Entity inventory grouped by type
  sections.push('## ENTITIES (' + entities.length + ' total)');

  const byType = new Map<EntityType, Entity[]>();
  for (const e of entities) {
    const list = byType.get(e.type) ?? [];
    list.push(e);
    byType.set(e.type, list);
  }

  for (const [type, group] of byType) {
    sections.push(`\n### ${type} (${group.length})`);
    for (const e of group) {
      const nato = e.corroboration.natoRating;
      const sources = e.corroboration.sources.length;
      let line = `- [${e.id}] ${e.name}`;
      if (e.description) line += ` — ${e.description}`;
      line += ` (NATO: ${nato}, sources: ${sources}, confidence: ${e.confidence.toFixed(2)})`;

      // Include key identifiers from properties
      const props = e.properties;
      if (props.mmsi) line += ` MMSI:${props.mmsi}`;
      if (props.imo) line += ` IMO:${props.imo}`;
      if (props.icao24) line += ` ICAO24:${props.icao24}`;
      if (props.coordinates) line += ` coords:${JSON.stringify(props.coordinates)}`;

      sections.push(line);
    }
  }

  // Relationships
  if (relationships.length > 0) {
    sections.push('\n## RELATIONSHIPS (' + relationships.length + ' total)');

    // Build entity name lookup
    const nameMap = new Map<string, string>();
    for (const e of entities) nameMap.set(e.id, e.name);

    for (const r of relationships) {
      const src = nameMap.get(r.sourceEntityId) ?? r.sourceEntityId;
      const tgt = nameMap.get(r.targetEntityId) ?? r.targetEntityId;
      sections.push(
        `- ${src} —[${r.type}]→ ${tgt} (confidence: ${r.confidence.toFixed(2)})`,
      );
    }
  }

  // Coverage
  if (coverage) {
    sections.push('\n## COVERAGE');
    sections.push(`Overall: ${coverage.overallPercentage}%`);
    for (const p of coverage.parameters) {
      const status = p.corroborated ? 'CORROBORATED' : p.addressed ? 'ADDRESSED' : 'NOT ADDRESSED';
      sections.push(`- ${p.name}: ${status} — ${p.detail}`);
    }
  }

  return sections.join('\n');
}

/**
 * Build the assessment template that instructs the LLM on required sections.
 */
export function buildAssessmentTemplate(deskConfig: DeskConfig): string {
  return [
    'Generate an intelligence assessment with the following sections:',
    '',
    '1. SITUATION OVERVIEW',
    '   Brief context of what was investigated and why.',
    '   Cite relevant entity IDs.',
    '',
    '2. KEY FINDINGS',
    '   Most significant discoveries from the entity graph.',
    '   Each finding must reference at least one entity ID.',
    '',
    '3. ENTITIES OF INTEREST',
    '   Entities with highest significance or lowest credibility ratings.',
    '   Include NATO ratings and provenance notes.',
    '',
    '4. RELATIONSHIPS & PATTERNS',
    '   Notable connections, clusters, or patterns in the graph.',
    '   Reference both entity IDs involved in each relationship.',
    '',
    '5. INDICATOR COVERAGE',
    '   Which desk priority indicators were addressed by the findings.',
    `   Desk: ${deskConfig.name}`,
    '',
    '6. INFORMATION GAPS',
    '   What the investigation did NOT find or could not confirm.',
    '   Single-source entities that need further corroboration.',
    '',
    '7. RECOMMENDATIONS',
    '   Suggested follow-up actions for the analyst.',
    '',
    'IMPORTANT:',
    '- Every factual claim MUST cite at least one entity ID in brackets, e.g. [entity_id].',
    '- If data is missing for a section, state "No data available for this section."',
    '- Be precise, professional, and concise.',
    '- This is a MACHINE-GENERATED DRAFT requiring analyst review.',
  ].join('\n');
}

// ---------------------------------------------------------------------------
// Synthesis agent
// ---------------------------------------------------------------------------

export interface SynthesisAgentConfig {
  /** Maximum output tokens for the synthesis call. Default: 4096. */
  maxOutputTokens?: number;
}

/**
 * Run the synthesis agent to generate a draft assessment.
 *
 * @param adapter - LLM adapter instance (uses Opus via synthesize method)
 * @param investigationId - ID of the investigation being synthesized
 * @param entities - All validated entities from the investigation
 * @param relationships - All relationships from the entity graph
 * @param coverage - Coverage report from the corroboration phase
 * @param deskConfig - Active desk configuration
 * @returns DraftAssessment with provenance enforcement
 */
export async function runSynthesis(
  adapter: LLMAdapter,
  investigationId: string,
  entities: Entity[],
  relationships: Relationship[],
  coverage: CoverageReport | null,
  deskConfig: DeskConfig,
): Promise<DraftAssessment> {
  if (entities.length === 0) {
    return {
      investigationId,
      executiveSummary: 'No entities were extracted during this investigation. No assessment can be generated.',
      sections: [],
      generatedAt: new Date().toISOString(),
      modelUsed: 'none',
      tokenCost: 0,
    };
  }

  const graphSummary = buildGraphSummary(entities, relationships, coverage);
  const template = buildAssessmentTemplate(deskConfig);

  const result = await adapter.synthesize(graphSummary, template);

  // Validate the raw assessment
  const raw = result.data;
  const sections = validateSections(raw.sections, entities);

  const assessment: DraftAssessment = {
    investigationId,
    executiveSummary: sanitizeText(raw.executiveSummary || 'Assessment generation produced no executive summary.'),
    sections,
    generatedAt: new Date().toISOString(),
    modelUsed: result.model,
    tokenCost: result.tokensUsed,
  };

  // Save assessment version for revision tracking
  try {
    await saveAssessmentVersion(investigationId, assessment as unknown as Record<string, unknown>, 'system');
  } catch {
    // Non-fatal — assessment still returned even if version tracking fails
    console.warn('[report-writer] Failed to save assessment version');
  }

  return assessment;
}

// ---------------------------------------------------------------------------
// Validation helpers
// ---------------------------------------------------------------------------

/**
 * Validate and sanitize assessment sections.
 * Ensures each section has a title, content, and citation references
 * that correspond to actual entity IDs.
 */
function validateSections(
  rawSections: RawDraftAssessment['sections'],
  entities: Entity[],
): AssessmentSection[] {
  if (!Array.isArray(rawSections) || rawSections.length === 0) {
    return [{
      title: 'Assessment',
      content: 'The synthesis model did not produce structured sections.',
      citations: [],
    }];
  }

  const entityIds = new Set(entities.map(e => e.id));

  return rawSections.map(section => {
    // Filter citations to only valid entity IDs
    const validCitations = Array.isArray(section.citations)
      ? section.citations.filter(id => entityIds.has(id))
      : [];

    let content = sanitizeText(section.content || '');

    // Flag sections with content but zero valid citations — provenance enforcement
    if (content.length > 0 && validCitations.length === 0) {
      content += '\n\n[WARNING: This section contains no entity citations. Claims require analyst verification.]';
    }

    return {
      title: sanitizeText(section.title || 'Untitled Section'),
      content,
      citations: validCitations,
    };
  });
}

/**
 * Basic text sanitization — strip control characters, limit length.
 */
function sanitizeText(text: string): string {
  // Strip control characters except newlines and tabs
  // eslint-disable-next-line no-control-regex
  return text.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '').trim();
}
